import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  useContextTree,
  useCreateWriteKey,
  useRevokeWriteKey,
  useSetSdkEnabled,
  useWriteKeys,
  type Environment,
  type WriteKey,
} from '@/lib/queries';
import { useAuthStore } from '@/stores/auth';
import { useContextStore } from '@/stores/context';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Check, Copy, Plus, TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

export const Route = createFileRoute('/_authed/settings')({ component: SettingsPage });

function SettingsPage(): React.JSX.Element {
  return (
    <div className="space-y-6">
      <h1 className="text-xl font-semibold tracking-tight">Settings</h1>

      <Tabs defaultValue="keys">
        <TabsList>
          <TabsTrigger value="keys">Write keys</TabsTrigger>
          <TabsTrigger value="environments">Environments</TabsTrigger>
          <TabsTrigger value="profile">Profile</TabsTrigger>
        </TabsList>

        <TabsContent value="keys" className="mt-6">
          <WriteKeysTab />
        </TabsContent>
        <TabsContent value="environments" className="mt-6">
          <EnvironmentsTab />
        </TabsContent>
        <TabsContent value="profile" className="mt-6">
          <ProfileTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ---------------------------------------------------------------------------- write keys

function WriteKeysTab(): React.JSX.Element {
  const environmentId = useContextStore((s) => s.environmentId);
  const { data: keys, isLoading } = useWriteKeys(environmentId);
  const create = useCreateWriteKey(environmentId);
  const revoke = useRevokeWriteKey(environmentId);

  // The raw key exists only in the create response — the server stores a hash and never returns it
  // again. Holding it in state for the length of one dialog is the entire reveal-once flow.
  const [revealed, setRevealed] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [pendingRevoke, setPendingRevoke] = useState<WriteKey | null>(null);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Write keys authenticate the SDK against this environment.
        </p>
        <Button
          disabled={!environmentId || create.isPending}
          onClick={() =>
            create.mutate(undefined, {
              onSuccess: (key) => {
                setCopied(false);
                setRevealed(key.key);
              },
              onError: (error: Error) => toast.error(error.message),
            })
          }
        >
          <Plus className="size-4" />
          Create key
        </Button>
      </div>

      {isLoading ? (
        <Skeleton className="h-32 rounded-xl" />
      ) : (
        <div className="rounded-xl border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Key ID</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-0" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {!keys?.length ? (
                <TableRow>
                  <TableCell colSpan={4} className="py-10 text-center text-sm text-muted-foreground">
                    No write keys in this environment yet.
                  </TableCell>
                </TableRow>
              ) : (
                keys.map((key) => (
                  <TableRow key={key.id}>
                    <TableCell className="font-mono text-xs">{key.id}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {new Date(key.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {key.revoked_at ? (
                        <Badge variant="secondary">Revoked</Badge>
                      ) : (
                        <Badge variant="outline">Active</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {!key.revoked_at && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => setPendingRevoke(key)}
                        >
                          Revoke
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={revealed !== null} onOpenChange={(open) => !open && setRevealed(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Copy your write key now</DialogTitle>
            <DialogDescription>
              This is the only time this key will ever be shown. We store a hash of it — if you lose it,
              revoke it and create another.
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center gap-2">
            <code className="min-w-0 flex-1 truncate rounded-md border bg-secondary/40 px-3 py-2 font-mono text-xs">
              {revealed}
            </code>
            <Button
              variant="outline"
              size="icon"
              aria-label="Copy write key"
              onClick={async () => {
                await navigator.clipboard.writeText(revealed ?? '');
                setCopied(true);
                toast.success('Write key copied');
              }}
            >
              {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
            </Button>
          </div>

          <DialogFooter>
            <Button onClick={() => setRevealed(null)}>I have copied it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={pendingRevoke !== null} onOpenChange={(open) => !open && setPendingRevoke(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke this write key?</AlertDialogTitle>
            <AlertDialogDescription>
              Every SDK build shipping this key stops authenticating immediately, including apps already on
              devices. The key stays listed as revoked; it cannot be restored.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-white hover:bg-destructive/90"
              onClick={() => {
                const key = pendingRevoke;
                if (!key) return;
                revoke.mutate(key.id, {
                  onSuccess: () => toast.success('Write key revoked'),
                  onError: (error: Error) => toast.error(error.message),
                });
                setPendingRevoke(null);
              }}
            >
              Revoke key
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---------------------------------------------------------------------------- environments

function EnvironmentsTab(): React.JSX.Element {
  const appId = useContextStore((s) => s.appId);
  const { data: orgs, isLoading } = useContextTree();
  const setSdkEnabled = useSetSdkEnabled();

  const [pendingKill, setPendingKill] = useState<Environment | null>(null);
  const [typed, setTyped] = useState('');

  const app = orgs
    ?.flatMap((org) => org.workspaces)
    .flatMap((workspace) => workspace.apps)
    .find((candidate) => candidate.id === appId);

  if (isLoading) return <Skeleton className="h-40 rounded-xl" />;

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Turning the SDK off stops delivery to every device in that environment. It is a kill switch, not a
        pause.
      </p>

      <div className="rounded-xl border divide-y">
        {app?.environments.map((environment) => (
          <div key={environment.id} className="flex items-center justify-between gap-4 px-4 py-3">
            <div>
              <p className="text-sm font-medium capitalize">{environment.name}</p>
              <p className="text-xs text-muted-foreground">
                {environment.sdk_enabled ? 'SDK enabled — bundles are being served.' : 'SDK disabled — no experience reaches a device.'}
              </p>
            </div>
            <Switch
              checked={environment.sdk_enabled}
              aria-label={`SDK enabled for ${environment.name}`}
              onCheckedChange={(next) => {
                // Only the OFF direction is destructive; turning delivery back on needs no ceremony.
                if (!next) {
                  setTyped('');
                  setPendingKill(environment);
                  return;
                }
                setSdkEnabled.mutate(
                  { id: environment.id, sdkEnabled: true },
                  {
                    onSuccess: () => toast.success(`SDK enabled for ${environment.name}`),
                    onError: (error: Error) => toast.error(error.message),
                  }
                );
              }}
            />
          </div>
        ))}
        {!app?.environments.length && (
          <p className="px-4 py-10 text-center text-sm text-muted-foreground">
            This app has no environments.
          </p>
        )}
      </div>

      <AlertDialog open={pendingKill !== null} onOpenChange={(open) => !open && setPendingKill(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <TriangleAlert className="size-4" />
              Disable the SDK in {pendingKill?.name}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Every SDK in <span className="font-medium text-foreground">{pendingKill?.name}</span> stops
              immediately. No campaign, no experience, no bundle reaches any device in this environment until
              you turn it back on. Live users see nothing.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-2">
            <Label htmlFor="kill-confirm">
              Type <span className="font-mono font-medium text-foreground">{pendingKill?.name}</span> to
              confirm
            </Label>
            <Input
              id="kill-confirm"
              autoComplete="off"
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
            />
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={typed !== pendingKill?.name}
              className="bg-destructive text-white hover:bg-destructive/90"
              onClick={() => {
                const environment = pendingKill;
                if (!environment) return;
                setSdkEnabled.mutate(
                  { id: environment.id, sdkEnabled: false },
                  {
                    onSuccess: () => toast.success(`SDK disabled for ${environment.name}`),
                    onError: (error: Error) => toast.error(error.message),
                  }
                );
                setPendingKill(null);
              }}
            >
              Disable the SDK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---------------------------------------------------------------------------- profile

function ProfileTab(): React.JSX.Element {
  const navigate = useNavigate();
  const { user, signOut } = useAuthStore();
  const resetContext = useContextStore((s) => s.reset);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Profile</CardTitle>
        <CardDescription>Signed in as {user?.email}</CardDescription>
      </CardHeader>
      <CardContent>
        <Button
          variant="outline"
          onClick={async () => {
            await signOut();
            resetContext();
            await navigate({ to: '/login' });
          }}
        >
          Sign out
        </Button>
      </CardContent>
    </Card>
  );
}
